<i18n>
en:
  reserve:
    title: "Reservation contents"
    msg: "Please confirm the following contents before applying."
  checkin:
    label: "Check-in date"
  days:
    label: "Days"
    unit: " "
  time:
    label: "Visit time"
  luggage:
    label: "Luggage"
    unit: " "
  fee:
    label: "The amount of money for payment:"
    unit: "JPY"
  payment:
    title: "Payment"
  credit:
    label: "Credit No."
    hint: "Only numbers without hyphens"
  security:
    label: "Security code"
  expire:
    month:
      label: "Expire - Month"
    year:
      label: "Year"
  holderName:
    first:
      label: "Holder name - First name"
    name:
      label: "Last name"
  cancel:
    policy: "Cannot be canceled on the day of booking"
  back: "Back"
  submit: "Reserve"
  error:
    payToken: "Credit verification failed.<br>Please check the card information."
ja:
  reserve:
    title: "予約内容"
    msg: "以下の内容を確認の上、お申し込みをお願いいたします。"
  checkin:
    label: "チェックイン"
  days:
    label: "利用日数"
    unit: "日"
  time:
    label: "来店時間"
  luggage:
    label: "荷物"
    unit: "個"
  fee:
    label: "お支払い金額："
    unit: "円"
  payment:
    title: "クレジット支払情報"
  credit:
    label: "クレジットカード番号"
    hint: "ハイフンなしの数字のみで入力してください"
  security:
    label: "セキュリティーコード"
  expire:
    month:
      label: "有効期限 - 月"
    year:
      label: "年"
  holderName:
    first:
      label: "名義 - 名"
    name:
      label: "名義 - 性"
  cancel:
    policy: "キャンセルはチェックイン日の前日までとなります。<br>チェックイン当日はキャンセルできません。"
  back: "戻る"
  submit: "予約申込"
  error:
    payToken: "クレジットの認証に失敗しました。<br>カード情報を確認してください。"
</i18n>

<template lang="pug">
  div.my-6
    v-container
      //- Propsエラー
      template(v-if="errProps")
        v-row
          v-col(cols="12" align="center")
            v-alert.d-inline-block(dense outlined type="error")
              div(v-html="$t('error.SYSERR')")

      //- 読込中
      template(v-else-if="!shop || !canScript")
        //- エラーメッセージ
        v-row(v-if="errMsg")
          v-col(cols="12" align="center")
            v-alert.d-inline-block(dense outlined type="error")
              div(v-html="errMsg")

        //- 読込中
        v-row(v-else)
          v-col.my-12.pa-12(cols="12")
            v-progress-linear(indeterminate)

      //- コンテンツ
      template(v-else)
        v-form.px-md-8.px-lg-10.px-xl-12(onsubmit="return false")
          //-  店舗情報
          v-row(justify="center")
            v-col(cols="12" align="center")
              h2|{{shop.shopName}}

          //- 予約情報
          v-row(justify="center")
            v-col(cols="12")
              v-alert(dense outlined color="black" type="info")
                h3|{{$t('reserve.title')}}
                p|{{$t('reserve.msg')}}
                v-alert(text border="bottom" color="primary" align="center")
                  |{{$t('fee.label')}}
                  |{{usageFee | price}}{{$t('fee.unit')}}

                v-row(justify="center")
                  v-col(cols="12" sm="6" md="4" xl="2")
                    v-text-field(prepend-icon="fa-calendar-day" :label="$t('checkin.label')" v-bind:value="checkinDate | date" readonly)

                v-row(justify="center")
                  v-col(cols="12" sm="6" md="4" xl="2")
                    v-text-field(prepend-icon="fa-calendar-alt" :label="$t('days.label')" v-bind:value="`${day} ${$t('days.unit')}`" readonly)

                v-row(justify="center")
                  v-col(cols="12" sm="6" md="4" xl="2")
                    v-text-field(prepend-icon="fa-clock" :label="$t('time.label')" v-bind:value="checkinTime | time" readonly)

                v-row(justify="center")
                  v-col(cols="12" sm="6" md="4" xl="2")
                    v-text-field(prepend-icon="fa-suitcase-rolling" :label="$t('luggage.label')" v-bind:value="`${number} ${$t('luggage.unit')}`" readonly)

          //- クレジット支払情報の入力
          v-form.px-md-8.px-lg-10.px-xl-12(onsubmit="return false" v-model="valid")
            v-row(justify="center")
              v-col.mt-8.pb-0(cols="12" align="center")
                h3|{{$t('payment.title')}}

            v-row(justify="center")
              v-col(cols="12" sm="8" md="6" xl="4")
                v-text-field.required(type="tel" prepend-icon="fa-credit-card" :label="$t('credit.label')" persistent-hint :hint="$t('credit.hint')" :rules="v($t('credit.label'), ['required', 'number'])" maxlength="16" v-model.number="forms.credit")
              v-col(cols="12" sm="4" md="4" xl="2")
                v-text-field.required(type="tel" prepend-icon="fa-shield-alt" :label="$t('security.label')" :rules="v($t('security.label'), ['required', 'number'])" maxlength="3" v-model.number="forms.security")

            v-row(justify="center")
              v-col(cols="12" sm="4" md="3" xl="2")
                v-select.required(prepend-icon="fa-calendar-check" :label="$t('expire.month.label')" :items="expireMonths" :rules="v($t('expire.month.label'), ['required'])" v-model="forms.expireMonth")
              v-col(cols="12" sm="3" md="2" xl="1")
                v-select.ml-8.ml-sm-0.required(:label="$t('expire.year.label')" :items="expireYears" :rules="v($t('expire.year.label'), ['required'])" v-model="forms.expireYear")

            v-row(justify="center")
              v-col(cols="12" sm="6" md="5" xl="4")
                v-text-field.required(prepend-icon="fa-signature" :label="$t('holderName.first.label')" :rules="v($t('holderName.first.label'), ['required', 'alphabet'])" maxlength="24" v-model="forms.holderFirstName")
              v-col(cols="12" sm="6" md="5" xl="4")
                v-text-field.ml-8.ml-sm-0.required(:label="$t('holderName.name.label')" :rules="v($t('holderName.name.label'), ['required', 'alphabet'])" maxlength="24" v-model="forms.holderLastName")

            v-col(cols="12" align="center")
              v-alert.d-inline-block(dense outlined type="warning")
                div(v-html="$t('cancel.policy')")

            //- 送信ボタン
            v-row(justify="center")
              v-col(cols="12" sm="4" md="3" xl="2")
                v-btn(x-large block color="secondary" @click="back")|{{$t('back')}}
              v-col(cols="12" sm="4" md="3" xl="2")
                v-btn(x-large block type="submit" color="primary" :loading="isSubmit" @click="submit" :disabled="!valid")|{{$t('submit')}}

          //- エラーメッセージ
          v-row(v-if="errMsg")
            v-col(cols="12" align="center")
              v-alert.d-inline-block(dense outlined type="error")
                div(v-html="errMsg")
</template>

<script>
export default {
  head: {
    title: {
      inner: 'It will be a pleasure'
    }
  },
  data () {
    return {
      // 入力フォーム
      forms: {
        credit: null,
        security: null,
        expireYear: null,
        expireMonth: null,
        holderFirstName: null,
        holderLastName: null
      },
      // 入力バリデーション
      valid: false,
      // スクリプトの実行許可
      canScript: false,
      // 送信中
      isSubmit: false,
      // propsのエラー有無
      errProps: false,
      // エラーメッセージ
      errMsg: null,
      // 有効期限（月）
      expireMonths: []
    }
  },
  props: {
    // 店舗
    shop: {},
    // チェックイン日付
    checkinDate: {},
    // 来店時間
    checkinTime: {},
    // 日数
    day: {},
    // 荷物数
    number: {},
    // 利用金額
    usageFee: {}
  },
  created () {
    this.errProps = (!this.checkinDate || !this.checkinTime || !this.day || !this.number || !this.usageFee)
    this.expireMonths = [...Array(12).keys()].map(i => ('00' + ++i).slice(-2))
  },
  mounted () {
    this.includeScript()
  },
  computed: {
    /**
     * 有効期限（年）
     */
    expireYears () {
      const max = 10
      const dt = this.utils.date.nowDatetime()
      const year = dt.getFullYear()
      return [...Array(max).keys()].map(i => this.utils.cast.string(year + i++).slice(-2))
    }
  },
  methods: {
    /**
     * スクリプトをインクルード
     */
    includeScript () {
      this.canScript = false

      this.$loadScript('https://token.ccps.jp/UpcTokenPaymentMini.js')
        .then(() => {
          return this.$loadScript(`${process.env.VUE_APP_WEB_URL}js/pay.js`)
        })
        .then(() => {
          this.canScript = true
        })
        .catch(() => {
          this.errMsg = this.$t('error.SYSERR')
        })
    },
    /**
     * 送信処理
     */
    submit () {
      this.isSubmit = true

      this.createPayToken()
        .then(response => {
          return this.registReserve(response.tokenObject.token)
            .catch(() => {
              this.errMsg = this.$t('error.SYSERR')
            })
        })
        .catch(() => {
          this.errMsg = this.$t('error.payToken')
        })
        .finally(() => {
          this.isSubmit = false
        })
    },
    /**
     * 予約登録
     */
    registReserve (token) {
      return this.api.reserveRegist({
        shopId: this.shop.shopId,
        checkinDate: this.utils.cast.dateString(this.checkinDate),
        checkinTime: this.utils.cast.timeString(this.checkinTime),
        day: this.day,
        number: this.number,
        token
      })
        .then(response => {
          this.$router.push({name: 'ReserveComplete'})
        })
        .catch(response => {
          this.errMsg = this.$t(`error.${response.functionError.errorCode}`)
        })
    },
    /**
     * 支払トークンを生成
     */
    createPayToken () {
      return new Promise((resolve, reject) => {
        // eslint-disable-next-line no-undef
        pay_createToken(Multipayment, resolve, reject, {
          cardno: this.forms.credit,
          securitycode: this.forms.security,
          expire: this.utils.cast.string(this.forms.expireMonth) + this.utils.cast.string(this.forms.expireYear),
          holderfirstname: this.forms.holderFirstName,
          holderlastname: this.forms.holderLastName,
          email: 'test@example.com', // メール
          phonenumber: '09000000000' // 電話番号
        })
      })
    },
    /**
     * 予約画面に戻る
     */
    back () {
      this.$router.push({name: 'Reserve', params: this.$route.params})
    }
  }
}
</script>

<style lang="sass" scoped>
</style>
